/**
 * Control panel for Plone Foundation membership.
 *
 * Shows member counts, offers the voters' list for download, and runs the
 * renewal cycle steps the backend says apply now.
 * @module components/ControlPanel/MembershipControlPanel
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Container,
  Dimmer,
  Header,
  Item,
  Loader,
  Message,
  Segment,
  Statistic,
  Table,
} from 'semantic-ui-react';

import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import { useClient } from '@plone/volto/hooks/client/useClient';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Forbidden from '@plone/volto/components/theme/Forbidden/Forbidden';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import backSVG from '@plone/volto/icons/back.svg';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import downloadSVG from '@plone/volto/icons/download.svg';
import groupSVG from '@plone/volto/icons/group.svg';
import listSVG from '@plone/volto/icons/list-bullet.svg';
import playSVG from '@plone/volto/icons/play.svg';
import reloadSVG from '@plone/volto/icons/reload.svg';
import sendSVG from '@plone/volto/icons/send.svg';
import stopSVG from '@plone/volto/icons/stop.svg';
import worldSVG from '@plone/volto/icons/world.svg';

import {
  getMembership,
  getMembershipDownload,
  runMembershipAction,
} from '../../actions/membership';
import { downloadText } from '../../helpers/download';
import { initial } from '../../reducers/factory';
import type {
  MembershipAction,
  MembershipActionResult,
  MembershipDownload,
  MembershipDownloadFile,
  MembershipOverview,
  RequestState,
} from '../../types';
import ConfirmModal from './ConfirmModal';

const messages = defineMessages({
  title: {
    id: 'Foundation Membership',
    defaultMessage: 'Foundation Membership',
  },
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  overview: {
    id: 'Overview',
    defaultMessage: 'Overview',
  },
  currentMembers: {
    id: 'Current members',
    defaultMessage: 'Current members',
  },
  countries: {
    id: 'Countries',
    defaultMessage: 'Countries',
  },
  country: {
    id: 'Country',
    defaultMessage: 'Country',
  },
  allMembers: {
    id: 'All members',
    defaultMessage: 'All members',
  },
  states: {
    id: 'Membership states',
    defaultMessage: 'Membership states',
  },
  state: {
    id: 'State',
    defaultMessage: 'State',
  },
  members: {
    id: 'Members',
    defaultMessage: 'Members',
  },
  years: {
    id: 'Year joined',
    defaultMessage: 'Year joined',
  },
  year: {
    id: 'Year',
    defaultMessage: 'Year',
  },
  noCurrentMembers: {
    id: 'No current members.',
    defaultMessage: 'No current members.',
  },
  downloads: {
    id: 'Downloads',
    defaultMessage: 'Downloads',
  },
  download: {
    id: 'Download',
    defaultMessage: 'Download',
  },
  renewalCycle: {
    id: 'Renewal cycle',
    defaultMessage: 'Renewal cycle',
  },
  cycleOpen: {
    id: 'A renewal cycle is open.',
    defaultMessage: 'A renewal cycle is open.',
  },
  cycleClosed: {
    id: 'No renewal cycle is open.',
    defaultMessage: 'No renewal cycle is open.',
  },
  run: {
    id: 'Run',
    defaultMessage: 'Run',
  },
  actionResult: {
    id: 'Members moved: {transitioned}. Skipped: {skipped}.',
    defaultMessage: 'Members moved: {transitioned}. Skipped: {skipped}.',
  },
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  loadError: {
    id: 'The membership overview could not be loaded.',
    defaultMessage: 'The membership overview could not be loaded.',
  },
});

/** Icon of each renewal cycle step. */
const ACTION_ICONS: Record<string, string> = {
  'open-renewal-cycle': playSVG,
  'reminder-renewal-cycle': sendSVG,
  'close-renewal-cycle': stopSVG,
};

/** The store slices this panel reads. */
interface PanelState {
  userSession?: { token?: string | null };
  membership?: RequestState & { data: MembershipOverview | null };
}

/** The membership slice before its reducer is installed. */
const NOTHING = { ...initial, data: null };

/** An API error, as Volto's api middleware rejects with it. */
interface ApiError {
  status?: number;
  message?: string;
  response?: { body?: { message?: string } };
}

/**
 * Read the message out of an API error.
 *
 * @param error What the request was rejected with.
 * @returns The backend's message, else the error's own.
 */
export function errorMessage(error: unknown): string {
  const apiError = error as ApiError | null;
  return (
    apiError?.response?.body?.message ?? apiError?.message ?? String(error)
  );
}

/**
 * Section header with an icon.
 *
 * @param props The icon and the title.
 * @returns The header.
 */
const SectionHeader = ({ icon, title }: { icon: string; title: string }) => (
  <Header as="h2">
    <Icon name={icon} size="24px" className="section-icon" />
    {title}
  </Header>
);

/**
 * Plone Foundation membership control panel.
 *
 * @returns The panel; Unauthorized without a session or when the backend
 *   refuses, Forbidden when it forbids.
 */
const MembershipControlPanel = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isClient = useClient();
  const token = useSelector((state: PanelState) => state.userSession?.token);
  const { data, loaded, loading, error } = useSelector(
    (state: PanelState) => state.membership ?? NOTHING,
  );
  const [confirming, setConfirming] = useState<MembershipAction | null>(null);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(() => {
    dispatch(getMembership());
  }, [dispatch]);

  useEffect(() => {
    if (token) {
      load();
    }
  }, [token, load]);

  const fail = useCallback(
    (reason: unknown) => {
      toast.error(
        <Toast
          error
          title={intl.formatMessage(messages.error)}
          content={errorMessage(reason)}
        />,
      );
    },
    [intl],
  );

  const run = useCallback(() => {
    if (!confirming) {
      return;
    }
    setRunning(true);
    (
      dispatch(
        runMembershipAction(confirming.id),
      ) as unknown as Promise<MembershipActionResult>
    )
      .then((result) => {
        toast.success(
          <Toast
            success
            title={confirming.title}
            content={intl.formatMessage(messages.actionResult, {
              transitioned: result.transitioned,
              skipped: result.skipped,
            })}
          />,
        );
      })
      .catch(fail)
      .finally(() => {
        setRunning(false);
        setConfirming(null);
        load();
      });
  }, [confirming, dispatch, fail, intl, load]);

  const download = useCallback(
    (item: MembershipDownload) => {
      setDownloading(item.id);
      (
        dispatch(
          getMembershipDownload(item.id),
        ) as unknown as Promise<MembershipDownloadFile>
      )
        .then((file) =>
          downloadText(file.filename, file.data, file.content_type),
        )
        .catch(fail)
        .finally(() => setDownloading(null));
    },
    [dispatch, fail],
  );

  if (!token) {
    return <Unauthorized />;
  }
  const status = (error as ApiError | null)?.status;
  if (status === 401) {
    return <Unauthorized />;
  }
  if (status === 403) {
    return <Forbidden />;
  }

  const stats = loaded ? data?.stats : undefined;
  const current = stats?.countries.filter((country) => country.count > 0);
  const toolbar = isClient ? document.getElementById('toolbar') : null;

  return (
    <Container id="page-membership" className="controlpanel-membership">
      <Helmet title={intl.formatMessage(messages.title)} />
      <Segment.Group raised>
        <Segment className="primary">
          <FormattedMessage {...messages.title} />
        </Segment>

        {error ? (
          <Segment>
            <Message negative>
              <Message.Header>
                {intl.formatMessage(messages.loadError)}
              </Message.Header>
              <p>{errorMessage(error)}</p>
            </Message>
          </Segment>
        ) : null}

        <Segment className="membership-body">
          <Dimmer active={loading} inverted>
            <Loader />
          </Dimmer>

          {stats && data ? (
            <>
              <SectionHeader
                icon={groupSVG}
                title={intl.formatMessage(messages.overview)}
              />
              <Statistic.Group size="small" widths="two">
                <Statistic>
                  <Statistic.Value>{stats.total_members}</Statistic.Value>
                  <Statistic.Label>
                    {intl.formatMessage(messages.currentMembers)}
                  </Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>{stats.total_countries}</Statistic.Value>
                  <Statistic.Label>
                    {intl.formatMessage(messages.countries)}
                  </Statistic.Label>
                </Statistic>
              </Statistic.Group>

              <SectionHeader
                icon={listSVG}
                title={intl.formatMessage(messages.states)}
              />
              <Table compact unstackable className="membership-states">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>
                      {intl.formatMessage(messages.state)}
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">
                      {intl.formatMessage(messages.members)}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {stats.states.map((state) => (
                    <Table.Row key={state.id}>
                      <Table.Cell>{state.title}</Table.Cell>
                      <Table.Cell textAlign="right">{state.count}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              <SectionHeader
                icon={worldSVG}
                title={intl.formatMessage(messages.countries)}
              />
              {stats.countries.length ? (
                <Table compact unstackable className="membership-countries">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {intl.formatMessage(messages.country)}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">
                        {intl.formatMessage(messages.currentMembers)}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">
                        {intl.formatMessage(messages.allMembers)}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {stats.countries.map((country) => (
                      <Table.Row key={country.id}>
                        <Table.Cell>
                          {country.flag ? `${country.flag} ` : ''}
                          {country.title}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {country.count}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {country.count_all}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              ) : null}
              {!current?.length ? (
                <p>{intl.formatMessage(messages.noCurrentMembers)}</p>
              ) : null}

              <SectionHeader
                icon={calendarSVG}
                title={intl.formatMessage(messages.years)}
              />
              {stats.years.length ? (
                <Table compact unstackable className="membership-years">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {intl.formatMessage(messages.year)}
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">
                        {intl.formatMessage(messages.members)}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {stats.years.map((year) => (
                      <Table.Row key={year.id}>
                        <Table.Cell>{year.title}</Table.Cell>
                        <Table.Cell textAlign="right">{year.count}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              ) : (
                <p>{intl.formatMessage(messages.noCurrentMembers)}</p>
              )}

              {data.downloads.length ? (
                <>
                  <SectionHeader
                    icon={downloadSVG}
                    title={intl.formatMessage(messages.downloads)}
                  />
                  <Item.Group divided>
                    {data.downloads.map((item) => (
                      <Item key={item.id}>
                        <Item.Content>
                          <Item.Header>{item.title}</Item.Header>
                          <Item.Description>
                            {item.description}
                          </Item.Description>
                          <Item.Extra>
                            <Button
                              primary
                              floated="right"
                              loading={downloading === item.id}
                              disabled={downloading !== null}
                              onClick={() => download(item)}
                            >
                              <Icon name={downloadSVG} size="16px" />
                              {intl.formatMessage(messages.download)}
                            </Button>
                          </Item.Extra>
                        </Item.Content>
                      </Item>
                    ))}
                  </Item.Group>
                </>
              ) : null}

              <SectionHeader
                icon={reloadSVG}
                title={intl.formatMessage(messages.renewalCycle)}
              />
              <Message info={data.renewal_cycle_open}>
                {intl.formatMessage(
                  data.renewal_cycle_open
                    ? messages.cycleOpen
                    : messages.cycleClosed,
                )}
              </Message>
              <Item.Group divided>
                {data.actions.map((item) => (
                  <Item key={item.id}>
                    <Item.Content>
                      <Item.Header>{item.title}</Item.Header>
                      <Item.Description>{item.description}</Item.Description>
                      <Item.Extra>
                        <Button
                          floated="right"
                          disabled={running}
                          onClick={() => setConfirming(item)}
                        >
                          <Icon
                            name={ACTION_ICONS[item.id] ?? reloadSVG}
                            size="16px"
                          />
                          {intl.formatMessage(messages.run)}
                        </Button>
                      </Item.Extra>
                    </Item.Content>
                  </Item>
                ))}
              </Item.Group>
            </>
          ) : null}
        </Segment>
      </Segment.Group>

      <ConfirmModal
        open={confirming !== null}
        title={confirming?.title ?? ''}
        content={confirming?.description ?? ''}
        onConfirm={run}
        onCancel={() => setConfirming(null)}
        loading={running}
      />

      {toolbar
        ? createPortal(
            <Toolbar
              pathname={pathname}
              hideDefaultViewButtons
              inner={
                <Link to="/controlpanel" className="item">
                  <Icon
                    name={backSVG}
                    aria-label={intl.formatMessage(messages.back)}
                    className="contents circled"
                    size="30px"
                    title={intl.formatMessage(messages.back)}
                  />
                </Link>
              }
            />,
            toolbar,
          )
        : null}
    </Container>
  );
};

export default MembershipControlPanel;
